import { createMuiTheme } from '@material-ui/core/styles'

const palette = {
  type: 'light',
  iconWarning:{
    color: 'yellow',
  },
  primary: {
    main: '#009EE2',
    secondary: '#1C3059',
    tertiary: '#F3F3F3',
  },
  secondary: {
    main: '#F46A6A',
  },
  error: {
    main: '#F46A6A',
  },
  success: {
    main: '#4dbf7e',
  },
  background: {
    default: '#e9e9e9',
    paper: '#fff',
    paper2: '#f9f9f9',
    secondary: '#286796',
  },
  text: {
    primary: 'rgba(28,48,89,1)',
    secondary: 'rgba(28,48,89,.6)',
    tertiary: 'rgba(28,48,89,.3)',
  },
  status: [
    '#9AC355', // green
    '#F8CD46', // yellow
    '#F48024', // orange
    '#F46A6A', // red
    '#556482', // gray
  ],
  metrics: [
    '#fff', // white
    '#F8CD46', // yellow
    '#F48024', // orange
    '#F46A6A', // red
    '#556482', // gray
    '#888E91', // gray 2
    '#286796' // blue
  ],
  random: [
    '#7A7C9F',
    '#90779A',
    '#AD7FA0',
    '#AE9DAB',
    '#7C98B7',
    '#6A99A5',
    '#819984',
    '#9FA77D',
    '#989576',
    '#808587',
    '#ADA29D',
    '#9B8586',
    '#A88670',
    '#BC7E80',
  ],
  myChip: [
    'rgba(49, 117, 122, 0.7)', // cyan
    'rgba(0, 158, 226, 0.7)', // blue
    'rgba(217, 49, 138, 0.5)', // pink
    'rgba(154, 195, 85, 0.7)', // green
    'rgba(244, 106, 106, 0.7)', // salmon
  ],
  borderColors: {
    main: 'rgba(0, 0, 0, 0.23)',
  },
  scroll: '#009EE2',
  divider: 'rgba(0,158,226,.1)',
  hover: 'rgba(0,158,226,.03)',
  hover2: 'rgba(0,158,226,.15)',
  hover3: 'rgba(0,158,226,.3)',
  hover4: 'rgba(0,158,226,.6)',
  details: 'rgba(0,158,226,.1)',
  details2: 'rgba(0,158,226,.3)',
  highlightSearch: 'rgba(0,158,226,.3)',
  highlightSearchActive: 'rgba(0,158,226,.9)',
  icon: '#1C3059',
}

const shape = {
  borderRadius: 5,
  scrollSize: 18,
  scrollRadius: 5,
}

const font = {
  size: 14,
  weight: {
    light: 300,
    regular: 400,
    bold: 700,
    black: 900,
  },
  lineHeight: '1.5',
}

const typography = {
  useNextVariants: true,
  fontFamily: [
    'Roboto',
    'system-ui',
    '-apple-system',
    'BlinkMacSystemFont',
    'Poppins',
    'Segoe UI',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    'Fira Sans',
    'Droid Sans',
    'Helvetica Neue',
    'sans-serif',
  ].join(','),
  fontSize: font.size,
  lineHeight: font.lineHeight,
  fontWeightLight: font.weight.light,
  fontWeightRegular: font.weight.regular,
  fontWeightBold: font.weight.bold,
  fontWeightBlack: font.weight.black,
  h1: {
    fontSize: font.size + 14,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  h2: {
    fontSize: font.size + 12,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  h3: {
    fontSize: font.size + 10,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  h4: {
    fontSize: font.size + 8,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  h5: {
    fontSize: font.size + 6,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  h6: {
    fontSize: font.size + 4,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  subtitle1: {
    fontSize: font.size + 4,
    fontWeight: font.weight.regular,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  subtitle2: {
    fontSize: font.size + 2,
    fontWeight: font.weight.regular,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  body1: {
    fontSize: font.size,
    fontWeight: font.weight.regular,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  body2: {
    fontSize: font.size - 2,
    fontWeight: font.weight.regular,
    lineHeight: font.lineHeight,
    color: palette.text.secondary,
  },
  button: {
    fontSize: font.size,
    fontWeight: font.weight.bold,
    textTransform: 'none',
  },
  caption: {
    fontSize: font.size,
    fontWeight: font.weight.regular,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
  overline: {
    fontSize: font.size,
    fontWeight: font.weight.regular,
    lineHeight: font.lineHeight,
    color: palette.text.primary,
  },
}

const shadows = [
  'none',
  '0 1px 1px 0 rgba(0,0,0,.05)',
  '0 1px 1px 0 rgba(0,0,0,.05), 0 2px 2px 0 rgba(0,0,0,.05)',
  '0 1px 1px 0 rgba(0,0,0,.05), 0 3px 4px 0 rgba(0,0,0,.05)',
  '0 2px 3px 0 rgba(0,0,0,.05), 0 4px 5px 0 rgba(0,0,0,.05)',
  '0 3px 5px 0 rgba(0,0,0,.05), 0 6px 10px 0 rgba(0,0,0,.05)',
  '0 4px 5px 0 rgba(0,0,0,.05), 0 8px 10px 1px rgba(0,0,0,.05)',
  '0 4px 6px 0 rgba(0,0,0,.05), 0 9px 12px 1px rgba(0,0,0,.05)',
  '0 6px 9px 0 rgba(0,0,0,.05), 0 12px 17px 2px rgba(0,0,0,.05)',
  '0 8px 12px 0 rgba(0,0,0,.05), 0 16px 24px 2px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
  '0 12px 19px 0 rgba(0,0,0,.05), 0 24px 38px 3px rgba(0,0,0,.05)',
]

export const normal = createMuiTheme({
  shape,
  typography,
  palette,
  shadows,
  overrides: {
    MuiDrawer: {
      paper: {
        elevation: 3,
        backgroundColor: palette.primary.main,
      },
    },
    MuiSnackbarContent: {
      root: {
        margin: 20,
        backgroundColor: 'rgba(0,0,0,.8)',
        fontSize: typography.fontSize,
        color: '#fff',
      },
    },
    MuiListItemText: {
      primary: {
        fontWeight: typography.fontWeightBold,
        color: palette.text.primary,
      },
      secondary: {
        fontWeight: typography.fontWeightRegular,
        color: palette.text.primary,
      },
    },
    MuiPaper: {
      root: {
        overflow: 'hidden',
      },
    },
    MuiSvgIcon: {
      root: {},
    },
    MuiCardContent: {
      root: {
        '&:last-child': {
          paddingBottom: 16,
        },
      },
    },
    MuiDialog: {
      paper: {
        // backgroundColor: 'transparent',
        boxShadow: shadows[12],
        borderRadius: shape.borderRadius * 2,
      },
    },
    MuiDialogTitle: {
      root: {
        padding: 20,
        paddingTop: 10,
        paddingBottom: 10,
        // backgroundColor: '#fff',
        zIndex: 1000,
        '& h2': {
          margin: 0,
          padding: 0,
          // color: palette.primary.secondary,
        },
      },
    },
    MuiDialogContent: {
      root: {
        padding: 20,
        zIndex: 999,
        scrollBehavior: 'smooth',
        '&::-webkit-scrollbar': {
          width: shape.scrollSize,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: palette.scroll,
          borderRadius: shape.scrollRadius,
        },
      },
    },
    MuiDialogActions: {
      root: {
        borderTop: `1px solid ${palette.divider}`,
        alignItems: 'right',
        padding: 20,
        // backgroundColor: '#fff',
        zIndex: 999,
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: 'rgba(0,0,0,.4)',
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: 'rgba(0,0,0,.8)',
      },
    },
    MuiButton: {
      root: {
        // height: 42,
        minWidth: 'auto',
        whiteSpace: 'nowrap',
        fontSize: font.fontSize,
      },
    },
    MuiIconButton: {
      colorPrimary: {
        backgroundColor: palette.hover,
        '&:hover': {
          backgroundColor: palette.hover2,
        },
      },
    },
    MuiAlert: {
      message: {
        fontSize: font.size,
      },
    },
    MuiAvatar: {
      root: {
        fontSize: typography.fontSize,
      },
      colorDefault: {
        color: palette.primary.main,
      },
    },
    MuiTabs: {
      root: {
        minHeight: 42,
        maxHeight: 42,
        borderRadius: shape.borderRadius,
        border: `1px solid ${palette.primary.main}`,
      },
    },
    MuiTab: {
      root: {
        minHeight: 40,
        maxHeight: 40,
        minWidth: 0,
        '@media (min-width: 0px)': {
          minWidth: 0,
        },
      },
      textColorPrimary: {
        color: palette.primary.main,
        zIndex: 10,
        '&$selected': {
          color: '#fff',
        },
      },
    },
    PrivateTabIndicator: {
      root: {
        height: 40,
        zIndex: 9,
        borderRadius: shape.borderRadius / 2,
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 40,
      },
    },
    MuiInputBase: {
      root: {
        minHeight: 54,
        lineHeight: font.lineHeight,
      },
    },
  },
})
